// =============================================================================
// RunLots — the "Lots · by pallet, by day" section on a run.
//
// One group per LOT (a tipped received pallet, stored bin, delivery or batch),
// one row per SAST day under it, and under each day the products packed out of
// it — read from the run_lot_* views (mig 20261019100000) and joined in
// src/lib/runLots.ts. Anything packed or rejected without a lot shows on its
// own Unassigned row; it is never folded into a pallet.
//
// "Rejected ≈ N ctn" is a carton-EQUIVALENT (kg ÷ the lot's own kg per carton)
// and is always shown beside the kg it was derived from.
// =============================================================================
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import QueryStateGate from '@/components/QueryStateGate'
import { Button, Card } from '@/components/ui'
import { downloadCSV } from '@/lib/csv'
import { fmtDateZA } from '@/lib/dates'
import { packoutSettled } from '@/lib/runsRegister'
import {
  EMPTY_RUN_LOTS, buildRunLotsData, hasUnassigned, lotLabel, lotSubtitle, lotsCsv, productKey, productLabel,
  type LotDay, type LotGroup, type LotPackoutRow, type LotProductRow, type LotUnassignedRow, type RunLotsData,
} from '@/lib/runLots'

function useRunLots(runId: string) {
  return useQuery({
    queryKey: ['run-lots', runId],
    queryFn: async (): Promise<RunLotsData> => {
      const results = await Promise.all([
        supabase.from('run_lot_packout').select('*').eq('run_id', runId).order('first_tipped_at'),
        supabase.from('run_lot_packout_daily').select('*').eq('run_id', runId).order('day'),
        supabase.from('run_lot_products').select('*').eq('run_id', runId),
        supabase.from('run_lot_unassigned').select('*').eq('run_id', runId).maybeSingle(),
      ])
      const failed = results.find(r => r.error)
      if (failed) throw failed.error
      const [lots, days, products, unassigned] = results
      return buildRunLotsData(lots.data, days.data, products.data, unassigned.data)
    },
  })
}

const kg  = (v: number | null | undefined) => v == null ? '—' : `${Number(v).toFixed(0)} kg`
const ctn = (v: number | null | undefined) => v == null ? '—' : String(Math.round(Number(v)))

/** " ≈ 6 ctn" — the carton-equivalent, only when there is kg to derive it from. */
function Equiv({ kgValue, equiv }: { kgValue: number; equiv: number | null }) {
  if (equiv == null || Number(kgValue) <= 0) return null
  return <span className="text-fg-muted"> ≈ {Number(equiv).toFixed(0)} ctn</span>
}

/** " · 3 whole" — cartons condemned unopened, a count beside the kg. */
function Whole({ whole }: { whole?: number }) {
  if (whole == null || Number(whole) <= 0) return null
  return <span className="text-fg-muted"> · {ctn(whole)} whole</span>
}

/** "84 kg ≈ 6 ctn · 3 whole" — the kg is the fact, the cartons are the equivalent, whole is a count. */
function KgEquiv({ kgValue, equiv, whole }: { kgValue: number; equiv: number | null; whole?: number }) {
  return <>{kg(kgValue)}<Equiv kgValue={kgValue} equiv={equiv} /><Whole whole={whole} /></>
}

function PackoutCell({ pct, settled }: { pct: number | null; settled: boolean }) {
  if (pct == null) return <span className="text-fg-muted">—</span>
  // U17 UF-09: a live run is drawn neutral, never amber (packing lags tipping) and never green (a false "good").
  if (!settled) {
    return (
      <span className="inline-flex items-center gap-2 justify-end">
        <span className="w-14 h-1.5 rounded-full bg-raised overflow-hidden">
          <span className="block h-full bg-fg-muted" style={{ width: `${Math.min(100, pct)}%` }} />
        </span>
        <span className="tabular-nums text-fg">{Number(pct).toFixed(1)} %</span>
      </span>
    )
  }
  const low = pct < 85
  return (
    <span className="inline-flex items-center gap-2 justify-end">
      <span className="w-14 h-1.5 rounded-full bg-raised overflow-hidden">
        <span className={clsx('block h-full', low ? 'bg-amber-400' : 'bg-emerald-400')} style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
      <span className={clsx('tabular-nums', low ? 'text-amber-300' : 'text-emerald-300')}>{Number(pct).toFixed(1)} %</span>
    </span>
  )
}

function ProductChips({ products }: { products: LotProductRow[] }) {
  if (products.length === 0) return null
  return (
    <tr>
      <td colSpan={8} className="pl-8 pr-2 pb-2 pt-0">
        <div className="flex flex-wrap gap-1.5">
          {products.map(p => (
            <span key={productKey(p)}
              className="inline-flex items-baseline gap-1.5 rounded-full bg-success-soft border border-emerald-800/60 px-2.5 py-0.5 text-caption text-fg-secondary">
              {productLabel(p)} <b className="text-fg tabular-nums">{ctn(p.cartons)} ctn</b> <span className="text-fg-muted tabular-nums">{kg(p.kg)}</span>
            </span>
          ))}
        </div>
      </td>
    </tr>
  )
}

function DayRow({ d }: { d: LotDay }) {
  return (
    <>
      <tr className="text-fg-secondary">
        <td className="pl-8 pr-2 py-1.5">{fmtDateZA(d.day)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{ctn(d.cartons_tipped)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{kg(d.kg_tipped)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{ctn(d.cartons_packed)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{kg(d.kg_packed)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums"><KgEquiv kgValue={d.reject_kg} equiv={d.reject_ctn_equiv} whole={d.whole_cartons} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums"><KgEquiv kgValue={d.waste_kg} equiv={d.waste_ctn_equiv} /></td>
        <td className="px-2 py-1.5" />
      </tr>
      <ProductChips products={d.products} />
    </>
  )
}

function LotName({ lot }: { lot: LotPackoutRow }) {
  const label = lotLabel(lot)
  if (!lot.source_pallet_id) return <span className="font-mono text-cyan-300">{label}</span>
  return (
    <Link to={`/pallets/${lot.source_pallet_id}`}
      className="font-mono text-cyan-300 underline decoration-line underline-offset-2 hover:text-cyan-200">
      {label}
    </Link>
  )
}

function LotRows({ g, settled }: { g: LotGroup; settled: boolean }) {
  const l = g.lot
  return (
    <>
      <tr className="bg-surface font-semibold border-t border-line">
        <td className="px-2 py-2">
          <LotName lot={l} />
          <span className="text-fg-muted font-normal"> · {lotSubtitle(l)}</span>
        </td>
        <td className="px-2 py-2 text-right tabular-nums">{ctn(l.cartons_in)}</td>
        <td className="px-2 py-2 text-right tabular-nums">{kg(l.kg_in)}</td>
        <td className="px-2 py-2 text-right tabular-nums">{ctn(l.cartons_out)}</td>
        <td className="px-2 py-2 text-right tabular-nums">{kg(l.kg_out)}</td>
        <td className={clsx('px-2 py-2 text-right tabular-nums', Number(l.reject_kg) > 0 ? 'text-red-300' : 'text-fg-secondary')}><KgEquiv kgValue={l.reject_kg} equiv={l.reject_ctn_equiv} whole={l.whole_cartons} /></td>
        <td className="px-2 py-2 text-right tabular-nums text-fg-secondary"><KgEquiv kgValue={l.waste_kg} equiv={l.waste_ctn_equiv} /></td>
        <td className="px-2 py-2 text-right"><PackoutCell pct={l.packout_pct} settled={settled} /><span className="block text-caption text-fg-muted tabular-nums">{kg(l.unexplained_kg)} unexplained</span></td>
      </tr>
      {g.days.map(d => <DayRow key={d.day} d={d} />)}
    </>
  )
}

function UnassignedRow({ u }: { u: LotUnassignedRow }) {
  return (
    <tr className="bg-surface border-t border-line text-fg-secondary">
      <td className="px-2 py-2 font-semibold">Unassigned <span className="text-fg-muted font-normal">· packed or rejected with no lot</span></td>
      <td className="px-2 py-2" /><td className="px-2 py-2" />
      <td className="px-2 py-2 text-right tabular-nums">{ctn(u.cartons_out)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{kg(u.kg_out)}</td>
      <td className={clsx('px-2 py-2 text-right tabular-nums', Number(u.reject_kg) > 0 ? 'text-red-300' : 'text-fg-secondary')}><KgEquiv kgValue={u.reject_kg} equiv={null} whole={u.whole_cartons} /></td>
      <td className="px-2 py-2 text-right tabular-nums">{kg(u.waste_kg)}</td>
      <td className="px-2 py-2" />
    </tr>
  )
}

function LotsTable({ groups, unassigned, settled }: { groups: LotGroup[]; unassigned: LotUnassignedRow | null; settled: boolean }) {
  const th = 'px-2 py-1.5 text-right text-caption font-semibold uppercase tracking-wider text-fg-muted whitespace-nowrap'
  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <table className="min-w-[860px] w-full text-sm">
        <thead>
          <tr>
            <th className={clsx(th, 'text-left')}>Lot · day</th>
            <th className={th}>Ctns in</th><th className={th}>Kg in</th>
            <th className={th}>Ctns out</th><th className={th}>Kg out</th>
            <th className={th}>Rejected</th><th className={th}>Waste</th><th className={th}>Packout</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => <LotRows key={g.lot.lot_key} g={g} settled={settled} />)}
          {hasUnassigned(unassigned) && unassigned && <UnassignedRow u={unassigned} />}
        </tbody>
      </table>
    </div>
  )
}

function LotsHeader({ runNumber, data, compact }: { runNumber: string; data: RunLotsData; compact: boolean }) {
  const showCsv = !compact && data.groups.length > 0
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-caption font-semibold uppercase tracking-wider text-fg-muted">Lots · by pallet, by day</p>
        <p className="text-caption text-fg-muted">Each tipped pallet, bin, delivery or batch on its own. Rejected cartons are an equivalent: kg ÷ the lot's kg per carton.</p>
      </div>
      {showCsv && (
        <Button type="button" variant="secondary" size="sm" className="shrink-0" icon={<Download size={14} />}
          onClick={() => downloadCSV(`${runNumber}-lots.csv`, lotsCsv(runNumber, data.groups, data.unassigned))}>
          CSV
        </Button>
      )}
    </div>
  )
}

export function RunLotsSection({ runId, runNumber, status, compact }: {
  runId:     string
  runNumber: string
  /** Run status: amber and green on the packout appear only once it is settled (U17 UF-09). */
  status?:   string | null
  /** Print view: no export button, tighter card. */
  compact?:  boolean
}) {
  const q = useRunLots(runId)
  const data = q.data ?? EMPTY_RUN_LOTS
  return (
    <Card as="section" className={compact ? 'space-y-2' : 'space-y-3'}>
      <LotsHeader runNumber={runNumber} data={data} compact={!!compact} />
      {data.groups.length === 0
        ? <QueryStateGate query={q} noun="lots"
            empty={<p className="text-sm text-fg-muted py-1">No lots yet — a lot appears once a pallet, bin, delivery or batch is tipped into this run.</p>} />
        : <LotsTable groups={data.groups} unassigned={data.unassigned} settled={packoutSettled(status)} />}
    </Card>
  )
}
